'use client';

import { useRef } from 'react';
import Image from 'next/image';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { SectionHeading } from '@/components/shared/SectionHeading';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { Location } from '@/lib/content-schema';

const locationColors = ['#325443', '#919F64', '#896F5C'];

interface Props {
  heading: string;
  locations: Location[];
}

export function GeographicPresence({ heading, locations }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="donde-estamos" className="py-24 md:py-32 bg-zibarit-bone">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          title={heading}
          size="default"
        />

        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {locations.map((location, index) => (
            <LocationCard
              key={location.name}
              name={location.name}
              tagline={location.tagline}
              image={location.image}
              color={locationColors[index] || locationColors[0]}
              index={index}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function LocationCard({
  name,
  tagline,
  image,
  color,
  index,
}: {
  name: string;
  tagline: string;
  image: string;
  color: string;
  index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 1.1]);

  return (
    <motion.div
      ref={cardRef}
      variants={fadeInUp}
      className="relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden group"
    >
      {/* Background with parallax zoom */}
      <motion.div
        style={{ scale }}
        className="absolute inset-0 transition-all duration-700"
      >
        <Image
          src={image}
          alt={name}
          fill
          sizes="(max-width: 768px) 100vw, 33vw"
          className="object-cover"
          priority={index === 0}
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${color}20 0%, ${color}40 100%)`,
          }}
        />
      </motion.div>

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-zibarit-negro/70 via-transparent to-transparent group-hover:from-zibarit-negro/80 transition-colors duration-500" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-8">
        <h3 className="text-title text-3xl md:text-4xl text-zibarit-bone mb-2">
          {name}
        </h3>
        <p className="text-body text-sm text-zibarit-marron-secundario">{tagline}</p>
      </div>
    </motion.div>
  );
}
